/**
 * AI Voice Editing (CRE): collects a spoken edit instruction, sends it with the
 * current report to EditReportWithAI, and applies the returned operations to
 * the editor with the same highlight/undo behaviour as Process AI.
 *
 * Instruction source: the editor mic in AI Edit mode (AI Edit button / hotkey,
 * or "ערוך דוח" / "Edit report" while dictating). Speech is collected here and
 * sent after a short pause, or as soon as the mic stops / AI Edit ends.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { closeHistory } from "@tiptap/pm/history";
import { editReportWithAi } from "../../utils/API_SERVICE";
import {
  CONTENT_MODES,
  getReportHtmlSource,
  getSaveContentFromReport,
  processContent,
} from "../../utils/contentProcessor";
import { diffHtml } from "../../utils/htmlDiff";
import { getCrePermissionDeniedMessage } from "../crePermissions";
import { SKIP_REASON, SOFT_SKIP_REASONS, applyEditOperations } from "./applyEditOperations";
import {
  EDIT_INSTRUCTION_PAUSE_MS,
  EDIT_REPORT_AI_PERMISSION,
  MIN_INSTRUCTION_WORDS,
  buildEditReportRequest,
  normalizeEditResponse,
} from "./editReportAiContract";

function sealHistory(editor) {
  if (!editor?.view || !editor.state) return;
  editor.view.dispatch(closeHistory(editor.state.tr));
}

export const EDIT_AI_PHASE = { IDLE: "idle", COLLECTING: "collecting", SENDING: "sending" };

const IDLE = { phase: EDIT_AI_PHASE.IDLE, instruction: "" };

/**
 * @param {object} p
 * @param {boolean} p.enabled              CRE only
 * @param {React.RefObject} p.editorRef
 * @param {string} p.transcript            editor HTML state
 * @param {Function} p.setTranscript
 * @param {() => object} p.getContext      { email, userId, relatedUserId, organisationId, studyId, templateId, language, modelSpeed }
 * @param {(code: string) => boolean} p.requirePermission
 * @param {Function} p.t                   i18next t
 * @param {(outcome: string) => void} [p.onFinished] after each instruction:
 *   "applied" | "partial" | "not_applied" | "no_changes" | "clarification" | "failed"
 * @param {() => boolean} [p.isBlocked]    true while Process AI runs
 */
export function useEditReportWithAi({
  enabled,
  editorRef,
  transcript,
  setTranscript,
  getContext,
  requirePermission,
  t,
  onFinished,
  isBlocked,
}) {
  const [status, setStatus] = useState(IDLE);
  const bufferRef = useRef("");
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const latest = useRef({});
  latest.current = { enabled, transcript, setTranscript, getContext, requirePermission, t, onFinished, isBlocked };

  const tr = useCallback(
    (key, defaultValue, vars) =>
      latest.current.t ? latest.current.t(`editReportAi.${key}`, { defaultValue, ...vars }) : defaultValue,
    [],
  );

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const applyResult = useCallback(
    (result) => {
      const editor = editorRef.current?.editor;
      const L = latest.current;
      if (result.needsClarification) {
        toast.info(result.clarificationMessage || tr("clarify", "Please say more precisely what to change."), {
          autoClose: 10000,
        });
        return "clarification";
      }

      const sourceHtml = getReportHtmlSource({ editor, transcript: L.transcript });
      const cleanHtml = processContent(sourceHtml, CONTENT_MODES.SAVE) || "";
      const { html, applied, skipped } = applyEditOperations(cleanHtml, result.operations);

      // Diagnostics without report text: actions and skip reasons only.
      if (skipped.length || result.unsupported.length) {
        console.info("[EditReportWithAI] not applied", {
          skipped: skipped.map((s) => ({ action: s.op?.action, reason: s.reason })),
          unsupported: result.unsupported.map((u) => ({ action: u.action, reason: u.unsupported })),
        });
      }

      if (applied.length) {
        const highlighted = diffHtml(sourceHtml, html);
        L.setTranscript?.(highlighted);
        try {
          // One instruction = exactly one undo step, never merged with the
          // previous edit or dictation (ProseMirror groups close transactions).
          sealHistory(editor);
          editor?.commands?.setContent(highlighted, false);
          sealHistory(editor);
        } catch (_) {
          // State update above still applies.
        }
      }

      // "Nothing to do" (e.g. replaceAll with no match) is not a failure.
      const failed = skipped.filter((x) => !SOFT_SKIP_REASONS.has(x.reason));
      const notApplied = failed.length + result.unsupported.length;
      let outcome;
      if (applied.length && !notApplied) {
        outcome = "applied";
        toast.success(tr("applied", "Report updated"));
      } else if (applied.length) {
        outcome = "partial";
        toast.warning(
          tr("partlyApplied", "Report updated. {{count}} change(s) could not be applied.", { count: notApplied }),
        );
      } else if (failed.some((x) => x.reason === SKIP_REASON.HEADINGS_REMOVED)) {
        outcome = "not_applied";
        toast.warning(
          tr("sectionsProtected", "Not applied: this change would have removed report sections. Please say more precisely what to change."),
          { autoClose: 10000 },
        );
      } else if (notApplied) {
        outcome = "not_applied";
        toast.warning(tr("notApplied", "The requested change could not be applied to the report."));
      } else {
        outcome = "no_changes";
        toast.info(tr("noChanges", "No changes were needed."));
      }
      // API warnings are often clinical remarks about the dictated content
      // (live 22 Oct: "the new finding may conflict with the impression").
      // When the edit was applied they are not shown to the radiologist; they
      // are shown only when nothing was applied, where they explain why.
      if (result.warnings.length) {
        if (applied.length) console.info("[EditReportWithAI] warnings", { count: result.warnings.length });
        else toast.info(result.warnings.join("\n"), { autoClose: 8000 });
      }
      return outcome;
    },
    [editorRef, tr],
  );

  const send = useCallback(
    async (instruction) => {
      const L = latest.current;
      const editor = editorRef.current?.editor;
      const stop = () => {
        setStatus(IDLE);
        L.onFinished?.("failed");
      };
      if (!L.enabled || !editor) return stop();
      if (L.requirePermission && !L.requirePermission(EDIT_REPORT_AI_PERMISSION)) return stop();
      if (L.isBlocked?.()) {
        toast.info(tr("busy", "Please wait until Process AI has finished."));
        return stop();
      }

      const ctx = L.getContext ? L.getContext() : {};
      if (!ctx.organisationId) {
        toast.error(tr("selectOrganisation", "Please select an organisation first."));
        return stop();
      }
      const currentReportHtml = getSaveContentFromReport({ editor, transcript: L.transcript }) || "";
      if (!currentReportHtml.replace(/<[^>]*>/g, "").trim()) {
        toast.info(tr("emptyReport", "The report is empty. Dictate or run Process AI first."));
        return stop();
      }

      busyRef.current = true;
      setStatus({ phase: EDIT_AI_PHASE.SENDING, instruction });
      // Visible even when the mic popover is closed (mic already stopped).
      const progressToast = toast.info(tr("sending", "AI is editing the report…"), {
        autoClose: false,
        closeButton: false,
      });
      let outcome = "failed";
      try {
        const data = await editReportWithAi(
          buildEditReportRequest({ ...ctx, currentReportHtml, instruction }),
        );
        outcome = applyResult(normalizeEditResponse(data));
      } catch (err) {
        if (err?.code === "FEATURE_NOT_ALLOWED") {
          toast.error(getCrePermissionDeniedMessage(err));
        } else {
          toast.error(err?.message && err.message !== err.code ? err.message : tr("failed", "AI edit failed. Please try again."));
        }
      } finally {
        toast.dismiss(progressToast);
        busyRef.current = false;
        if (bufferRef.current.trim()) {
          // Speech that arrived while this edit was running is sent next.
          setStatus({ phase: EDIT_AI_PHASE.COLLECTING, instruction: bufferRef.current.trim() });
          clearTimer();
          timerRef.current = setTimeout(() => flushRef.current(), EDIT_INSTRUCTION_PAUSE_MS);
        } else {
          setStatus(IDLE);
        }
        latest.current.onFinished?.(outcome);
      }
    },
    [applyResult, editorRef, tr],
  );

  const flush = useCallback(() => {
    clearTimer();
    if (busyRef.current) return;
    const instruction = bufferRef.current.replace(/\s+/g, " ").trim();
    bufferRef.current = "";
    if (!instruction) {
      setStatus(IDLE);
      return;
    }
    const words = instruction.split(" ").filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
    if (words < MIN_INSTRUCTION_WORDS) {
      // Noise or a stray word while the mic is open: not an instruction.
      setStatus(IDLE);
      latest.current.onFinished?.("ignored");
      return;
    }
    void send(instruction);
  }, [send]);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  /** Called with Command-mode speech that matched no voice command. Returns true when taken. */
  const addInstructionText = useCallback((text) => {
    if (!latest.current.enabled) return false;
    const piece = String(text || "").trim();
    if (!piece) return false;
    bufferRef.current = `${bufferRef.current} ${piece}`.trim();
    if (!busyRef.current) {
      setStatus({ phase: EDIT_AI_PHASE.COLLECTING, instruction: bufferRef.current });
    }
    clearTimer();
    timerRef.current = setTimeout(() => flushRef.current(), EDIT_INSTRUCTION_PAUSE_MS);
    return true;
  }, []);

  const cancel = useCallback(() => {
    clearTimer();
    bufferRef.current = "";
    if (!busyRef.current) setStatus(IDLE);
  }, []);

  useEffect(() => () => clearTimer(), []);
  useEffect(() => {
    if (!enabled) cancel();
  }, [enabled, cancel]);

  /** True while an instruction is being collected or sent. */
  const isBusy = useCallback(() => busyRef.current || !!bufferRef.current.trim(), []);

  return { status, addInstructionText, flush, cancel, isBusy };
}

export default useEditReportWithAi;
